"use client";

import * as React from "react";

const KEYBOARD_OPEN_THRESHOLD_PX = 120;

function isTextInputTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.matches("input, textarea, select")
  );
}

/**
 * Detects a real mobile soft keyboard from the visual viewport height delta.
 * A focus event alone is insufficient because hardware keyboards do not resize
 * the viewport. Orientation changes reset the baseline to avoid false positives.
 */
export function useSoftKeyboardOpen(): boolean {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const viewport = window.visualViewport;
    const readHeight = () => viewport?.height ?? window.innerHeight;
    let baselineHeight = readHeight();

    const syncFromViewport = () => {
      const height = readHeight();
      baselineHeight = Math.max(baselineHeight, height);
      setIsOpen(baselineHeight - height > KEYBOARD_OPEN_THRESHOLD_PX);
    };

    const resetBaseline = () => {
      baselineHeight = readHeight();
      setIsOpen(false);
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (
        isTextInputTarget(event.target) &&
        baselineHeight - readHeight() > KEYBOARD_OPEN_THRESHOLD_PX
      ) {
        setIsOpen(true);
      }
    };

    const handleFocusOut = (event: FocusEvent) => {
      if (isTextInputTarget(event.target)) setIsOpen(false);
    };

    syncFromViewport();
    viewport?.addEventListener("resize", syncFromViewport);
    window.addEventListener("resize", syncFromViewport);
    window.screen?.orientation?.addEventListener("change", resetBaseline);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);

    return () => {
      viewport?.removeEventListener("resize", syncFromViewport);
      window.removeEventListener("resize", syncFromViewport);
      window.screen?.orientation?.removeEventListener("change", resetBaseline);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  return isOpen;
}
